import { useEffect, useState } from 'react';
import type { MediaPublic } from '../types';

/**
 * Répartit les œuvres en colonnes, DANS L'ORDRE DE LECTURE.
 *
 * POURQUOI NE PAS S'EN REMETTRE À CSS. La propriété `columns` compose un masonry
 * sans une ligne de JavaScript, mais elle remplit les colonnes VERTICALEMENT :
 * les premières œuvres descendent toute la colonne de gauche avant de passer à la
 * suivante. Sur le site d'origine, elles se lisent de gauche à droite — la
 * deuxième œuvre est à CÔTÉ de la première, pas en dessous.
 *
 * Ce n'est pas un détail d'affichage : Dorian a classé ses œuvres, et l'ordre dans
 * lequel on les découvre est le sien. Le respecter impose de répartir nous-mêmes.
 *
 * La distribution est un tour de rôle — l'œuvre i va dans la colonne i % n — et non
 * un placement dans la colonne la plus courte. L'équilibrage donnerait des colonnes
 * de hauteurs plus proches, mais au prix de l'ordre : une œuvre haute repousserait
 * les suivantes ailleurs. Entre des bas de colonnes irréguliers et un ordre
 * bousculé, c'est l'ordre qui compte.
 */
export function repartirEnColonnes<T>(elements: T[], nombre: number): T[][] {
  const colonnes: T[][] = Array.from({ length: nombre }, () => []);
  elements.forEach((element, index) => {
    colonnes[index % nombre]?.push(element);
  });
  return colonnes;
}

/** Nombre de colonnes selon la largeur, aligné sur les points de rupture du CSS. */
function nombreDeColonnes(): number {
  if (typeof window === 'undefined') return 5;
  if (window.matchMedia('(max-width: 640px)').matches) return 2;
  if (window.matchMedia('(max-width: 1024px)').matches) return 3;
  return 5;
}

export function useNombreDeColonnes(): number {
  const [nombre, setNombre] = useState(nombreDeColonnes);

  useEffect(() => {
    const recalculer = () => setNombre(nombreDeColonnes());
    recalculer();
    window.addEventListener('resize', recalculer);
    return () => window.removeEventListener('resize', recalculer);
  }, []);

  return nombre;
}

interface Props {
  medias: MediaPublic[];
  rendre: (media: MediaPublic, indexGlobal: number) => React.ReactNode;
}

export function Grille({ medias, rendre }: Props) {
  const nombre = useNombreDeColonnes();
  const colonnes = repartirEnColonnes(
    medias.map((media, index) => ({ media, index })),
    nombre,
  );

  return (
    <div className="grille">
      {colonnes.map((colonne, indexColonne) => (
        <div className="grille-colonne" key={indexColonne}>
          {colonne.map(({ media, index }) => rendre(media, index))}
        </div>
      ))}
    </div>
  );
}
